import * as React from "react";
import * as _ from "lodash";
import * as S from "@styled";
import { connect } from "react-redux";
import Select from "shared/components/select/Select";

import { State } from "../model/State";
import { DispatchType, Utilities } from "../../shared/utilities";
import MonacoEditor from "../../shared/monaco/components/MonacoEditor";
import { ParameterEditorMode } from "./ParameterInput";
import { createSnippet, deleteSnippet, editSnippet, UserSnippet } from "../actions/SnippetActions";
import { ChangeEvent } from "../../shared/model/Event";
import { Permissions } from "../../shared/model/AllPermissions";
import { Option } from "shared/model/Option";
import CustomModal from "shared/components/CustomModal";
import colors from "shared/styles/colors";
import Collapse from "@kunukn/react-collapse";
import CustomToggle from "shared/components/CustomToggle";
import useUserTheme from "shared/styles/hooks/useUserTheme";
import LabelWithTooltip from "shared/components/LabelWithTooltip";
import { ThemeType } from "shared/styles/theme";

const getSpecificLanguage = (language: string): ParameterEditorMode => {
    switch (language) {
        case "Text": {
            return ParameterEditorMode.handlebars;
        }
        case "XML": {
            return ParameterEditorMode.xml;
        }
        case "JSON": {
            return ParameterEditorMode.json;
        }
        case "SQL": {
            return ParameterEditorMode.sql;
        }
        case "Expression": {
            return ParameterEditorMode.csharp;
        }
        default: {
            return ParameterEditorMode.handlebars;
        }
    }
}

export const SnippetsPanel: React.FC<SnippetsPanelProps> = (props) => {

    const [theme] = useUserTheme();
    const specificLanguages = ["Text", "XML", "JSON", "SQL", "Expression"];

    const [showNewSnippetModal, setShowNewSnippetModal] = React.useState<boolean>(false);
    const [snippetName, setSnippetName] = React.useState<string>("");
    const [isSnippetShared, setIsSnippetShared] = React.useState<boolean>(false);
    const [snippetLanguage, setSnippetLanguage] = React.useState<string>("Text");
    const [snippet, setSnippet] = React.useState<string>("");

    const [nameError, setNameError] = React.useState<string>("");
    const [snippetError, setSnippetError] = React.useState<string>("");

    const [searchFilter, setSearchFilter] = React.useState<string>("");
    const [languageFilter, setLanguageFilter] = React.useState<string>("");

    const isDisabled = snippetName === "" || snippet === "" || (nameError !== "" || snippetError !== "");

    const handleShowCreateNewSnippet = () => {
        setShowNewSnippetModal(true);
    }

    const handleSearchFilter = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchFilter(e.target.value);
    }

    const handleLanguageFilter = (e: Option) => {
        setLanguageFilter(e.value)
    }

    const resetModalState = () => {
        setSnippetName("");
        setIsSnippetShared(false);
        setSnippetLanguage("Text");
        setSnippet("");
        setNameError("");
        setSnippetError("");
        setShowNewSnippetModal(false);
    }

    const renderNewSnippetModal = () => {

        const handleCreateSnippet = () => {
            const newSnippet: UserSnippet = {
                name: snippetName,
                isShared: isSnippetShared,
                languageMode: snippetLanguage,
                snippet
            }
            props.createSnippet(newSnippet);
            resetModalState();
        }

        const onHideHandler = () => resetModalState();

        const validateName = (candidate: string) => {
            const nameRegex = /^[a-zA-ZöäåüÖÄÅÜ][a-zA-Z0-9_öäåüÖÄÅÜ]*$/;

            if (!candidate) {
                return FRENDS.Localizations.Validation_FieldRequired;
            }
            if (candidate.length > 32) {
                return `${FRENDS.Localizations.Snippet} name cannot exceed 32 characters.`;
            }
            if (!nameRegex.test(candidate)) {
                return FRENDS.Localizations.EnvironmentVariables_KeyPattern;
            }
            if (props.snippets?.some((s) => s.name.toLowerCase() === candidate.toLowerCase())) {
                return FRENDS.Localizations.Validation_ElementsMustHaveUniqueNames;
            }
            return "";
        };

        const validateSnippet = (snippet: string) => {
            if (!snippet) {
                return FRENDS.Localizations.Validation_FieldRequired;
            }
            if (snippet.length > 2000) {
                return "Snippet length cannot exceed 2000 characters.";
            }
            return "";
        }

        const handleNewSnippetName = (e: ChangeEvent<HTMLInputElement>) => {
            const nameValidation = validateName(e.target.value);
            setNameError(nameValidation);
            setSnippetName(e.target.value)
        }

        const handleIsShared = () => {
            setIsSnippetShared(shared => !shared);
        }

        const handleSpecificLanguage = (language: string) => {
            setSnippetLanguage(language)
        }

        const handleSetSnippet = (value: string) => {
            const snippetValidation = validateSnippet(value);
            setSnippetError(snippetValidation);
            setSnippet(value)
        }

        const radioButtonLabelStyles: React.CSSProperties = { display: 'flex', alignItems: 'center', height: '25px', width: 'fit-content', gap: '8px' };

        return (
            <CustomModal
                label={FRENDS.Localizations.Snippet}
                onClose={onHideHandler}
                shouldCloseOnOverlayClick={false}
                show={true}
                size="small"
                modalTitle={<h2>{FRENDS.Localizations.CreateNew} {FRENDS.Localizations.Snippet}</h2>}
            >
                <S.FormGroup variant={nameError !== "" ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                    <S.Label>{FRENDS.Localizations.Snippet} name</S.Label>
                    <S.Input isFullWidth={true} type="text" id="snippet-name" placeholder="MySnippet" required={true} value={snippetName} onChange={handleNewSnippetName} />
                    {nameError !== "" ? <div className="error">{nameError}</div> : null}
                </S.FormGroup>
                <S.FormGroup>
                    <LabelWithTooltip
                        label={`Shared ${FRENDS.Localizations.Snippet}`}
                        description={`Shared ${FRENDS.Localizations.Snippets} are visible to all users.`}
                    />
                    <CustomToggle
                        checked={isSnippetShared}
                        onChange={handleIsShared}
                    />
                </S.FormGroup>
                <S.FormGroup>
                    <LabelWithTooltip
                        label={`Language specific ${FRENDS.Localizations.Snippet}`}
                        description={`Select which language the ${FRENDS.Localizations.Snippet} is for. Selecting All, the ${FRENDS.Localizations.Snippet} will show for all languages.`}
                    />
                    <div style={{ display: "block" }}>
                        <S.Label htmlFor="language-All" style={radioButtonLabelStyles}>
                            <S.Radio key={"All"} id="language-All" name="specificLanguage" checked={"All" === snippetLanguage} onChange={() => handleSpecificLanguage("All")} />
                            All
                        </S.Label>
                    </div>
                    {specificLanguages.map((language, i) => (
                        <div style={{ display: "block" }} key={i}>
                            <S.Label htmlFor={`language-${language}`} style={radioButtonLabelStyles}>
                                <S.Radio key={language} id={`language-${language}`} name="specificLanguage" checked={language === snippetLanguage} onChange={() => handleSpecificLanguage(language)} />
                                {language}
                            </S.Label>
                        </div>
                    ))}
                </S.FormGroup>
                <S.FormGroup variant={snippetError !== "" ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                    <S.Label>{FRENDS.Localizations.Snippet}</S.Label>
                    <S.ExpressionEditorContainer variant={snippetError !== "" ? S.ExpressionEditorContainerState.error : S.ExpressionEditorContainerState.primary}>
                        <MonacoEditor
                            onChange={handleSetSnippet}
                            onInstantChange={handleSetSnippet}
                            value={snippet}
                            mode={getSpecificLanguage(snippetLanguage)}
                            height={300}
                            theme={theme}
                        />
                    </S.ExpressionEditorContainer>
                    {snippetError !== "" ? <div className="error">{snippetError}</div> : null}
                </S.FormGroup>
                <S.ButtonGroup gap={12} $justifyContentEnd={true}>
                    <S.Button variant={S.ButtonType.bordered} onClick={onHideHandler}>{FRENDS.Localizations.Cancel}</S.Button>
                    <S.Button onClick={handleCreateSnippet} disabled={isDisabled} id="create-new-snippet">
                        {FRENDS.Localizations.CreateNew}
                    </S.Button>
                </S.ButtonGroup>
            </CustomModal >
        )
    }

    const memoizedSnippets = React.useMemo(() => {
        return props.snippets?.filter((x) => x.name.toLowerCase().includes(searchFilter.toLowerCase()) && x.languageMode.toLowerCase().includes(languageFilter.toLowerCase()));
    }, [props.snippets, languageFilter, searchFilter]);

    const allOption: Option = {
        label: "All",
        value: ""
    }

    const languageOptions: Option[] = specificLanguages.map((lang) => ({
        label: lang,
        value: lang
    })).concat(allOption);

    return (
        <S.ParameterFieldset>
            <S.FormGroup>
                <S.Button onClick={handleShowCreateNewSnippet} id="create-new-snippet-button">+ {FRENDS.Localizations.CreateNew} {FRENDS.Localizations.Snippet}</S.Button>
            </S.FormGroup>
            <S.FormGroup>
                <h3>Filters</h3>
            </S.FormGroup>
            <S.FormGroup>
                <S.Label>
                    {FRENDS.Localizations.Name}
                </S.Label>
                <S.SearchInput
                    isFullWidth={true}
                    value={searchFilter}
                    id="searchSnippet"
                    placeholder={`Filter ${FRENDS.Localizations.Snippets} by name`}
                    onChange={handleSearchFilter}
                />
            </S.FormGroup>
            <S.FormGroup>
                <S.Label>
                    Language
                </S.Label>
                <Select
                    id="filter-snippets-language-selector"
                    value={Utilities.valueFromId(languageOptions, languageFilter)}
                    options={languageOptions}
                    placeholder={`Filter ${FRENDS.Localizations.Snippets} by language`}
                    onChange={handleLanguageFilter}
                />
            </S.FormGroup>
            <hr />
            {!_.isEmpty(memoizedSnippets) ? memoizedSnippets?.map((snippet) => (
                <SnippetRow key={snippet.name} snippet={snippet} onEditSnippet={props.editSnippet} onDeleteSnippet={props.deleteSnippet} />
            )) : (
                <p style={{ color: colors.grey_1 }}>No {FRENDS.Localizations.Snippets} were found.</p>
            )}
            {showNewSnippetModal && renderNewSnippetModal()}
        </S.ParameterFieldset>
    );
}

export const SnippetRow: React.FC<ISnippetRow> = ({ snippet, onEditSnippet, onDeleteSnippet }) => {
    const [theme] = useUserTheme();
    const [isCollapsed, setIsCollapsed] = React.useState<boolean>(true);
    const [newSnippet, setNewSnippet] = React.useState<string>(snippet.snippet);
    const [isShared, setIsShared] = React.useState<boolean>(snippet.isShared);

    const currentUsername = document.getElementById("navigationbar")?.dataset.username;
    const permissions = Utilities.getAllUserPermissions();

    const inputRestriction = newSnippet === snippet.snippet && isShared === snippet.isShared;
    // Only Admins and the Snippet owner can delete a shared Snippet
    const snippetDeletionRestriction = !(
        // Users can delete their own snippets
        (snippet.owner == currentUsername) ||
        // Admins can delete shared snippets
        (snippet.isShared && permissions[Permissions.UserManagementAdmin])
    );
    const snippetEditingRestriction = snippet.owner !== currentUsername;

    const toggleCollapsedHandler = () => {
        setIsCollapsed(collapsed => !collapsed);
    }

    const handleSetSnippet = (value: string) => {
        setNewSnippet(value);
    }

    const handleIsShared = () => {
        setIsShared(shared => !shared);
    }

    const handleSaveChanges = () => {
        if (!inputRestriction && snippet.id !== undefined) {
            onEditSnippet(newSnippet, isShared, snippet.id)
        }
    }

    const handleDeleteSnippet = () => {
        const confirmDeletion = confirm(`Are you sure you want to delete ${snippet.name}?`)
        if (confirmDeletion && snippet.id !== undefined) {
            onDeleteSnippet(snippet.id);
        }
    }

    return (
        <S.ElementRowStyled variant={isCollapsed ? S.ElementRowVariant.primary : S.ElementRowVariant.selected} className={`snippet-${snippet.name}-selector snippet-row-selector`}>
            <S.ElementHeaderContainer onClick={toggleCollapsedHandler} className="pointer" id={`collapsePackageTaskRow-${snippet.name}`}>
                <S.Label className="pointer">
                    {snippet.isShared && (<>&nbsp;<i title={`This ${FRENDS.Localizations.Snippet} is shared by ${snippet.owner}`} className="ci-share_outline" style={{ color: theme === ThemeType.light ? colors.purplePrimary : colors.purpleTertiary }} /></>)}&nbsp;{snippet.name}
                    &nbsp;<i className={`${isCollapsed ? "ci-chevron_big_down" : "ci-chevron_big_up"}`}></i>
                </S.Label>
            </S.ElementHeaderContainer>
            <S.ElementContentContainer>
                <Collapse isOpen={!isCollapsed}>
                    <hr />
                    <S.FormGroup>
                        <S.FlexContainer justifySpaceBetween={true}>
                            <div style={{ display: "block" }}>
                                <LabelWithTooltip
                                    label={`Shared ${FRENDS.Localizations.Snippet}`}
                                    description={`Shared ${FRENDS.Localizations.Snippets} are visible to all users.`}
                                />
                                <CustomToggle
                                    $inline={false}
                                    disabled={snippetEditingRestriction}
                                    checked={isShared}
                                    onChange={handleIsShared}
                                />
                            </div>
                            {!snippetDeletionRestriction && (
                                <i onClick={handleDeleteSnippet} className="pointer ci-trash_full" style={{ color: theme === ThemeType.light ? colors.purplePrimary : colors.purpleTertiary }} />
                            )}
                        </S.FlexContainer>
                    </S.FormGroup>
                    <S.FormGroup>
                        <S.Label>{FRENDS.Localizations.Snippet}</S.Label>
                        <S.ExpressionEditorContainer>
                            <MonacoEditor
                                readOnly={snippetEditingRestriction}
                                onChange={handleSetSnippet}
                                onInstantChange={handleSetSnippet}
                                value={newSnippet}
                                theme={theme}
                                mode={getSpecificLanguage(snippet.languageMode)}
                                height={300}
                            />
                        </S.ExpressionEditorContainer>
                    </S.FormGroup>
                    <S.FormGroup>
                        <S.Button id={`save-changes-${snippet.name}`} disabled={inputRestriction} onClick={handleSaveChanges}>{FRENDS.Localizations.SaveChanges}</S.Button>
                    </S.FormGroup>
                </Collapse>
            </S.ElementContentContainer>
        </S.ElementRowStyled>
    )
}

interface ISnippetRow {
    snippet: UserSnippet;
    onEditSnippet: (newSnippet: string, isShared: boolean, id: number) => void;
    onDeleteSnippet: (id: number) => void;
}

interface SnippetsPanelStateProps {
    snippets?: UserSnippet[];
}

interface SnippetsPanelDispatchProps {
    createSnippet: (snippet: UserSnippet) => void;
    editSnippet: (newSnippet: string, isShared: boolean, id: number) => void;
    deleteSnippet: (id: number) => void;
}


export type SnippetsPanelProps = SnippetsPanelStateProps & SnippetsPanelDispatchProps;
const mapStateToProps = (state: State): SnippetsPanelStateProps => {
    return {
        snippets: state.snippets
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>): SnippetsPanelDispatchProps => {
    return {
        createSnippet: (snippet: UserSnippet) => dispatch(createSnippet(snippet)),
        editSnippet: (newSnippet: string, isShared: boolean, id: number) => dispatch(editSnippet(newSnippet, isShared, id)),
        deleteSnippet: (id: number) => dispatch(deleteSnippet(id))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SnippetsPanel);
